import { z } from 'zod'

/**
 * The signed-in user, as the SPA sees them — the response of `GET /api/auth/me`.
 *
 * This is a projection, not a mirror of the WorkOS user. `id` is our local `users.id` (the FK
 * app tables reference), deliberately not the WorkOS `user_xxx`: the browser has no reason to
 * learn the provider's identifiers, and keeping them server-side is what let the Clerk→WorkOS
 * pivot avoid touching every app table.
 */
export const sessionUserSchema = z.object({
  /** Local `users.id`. */
  id: z.string(),
  email: z.string().nullable(),
  name: z.string().nullable(),
  /** WorkOS RBAC: the active organization's role slug, if any. */
  role: z.string().nullable(),
  /** WorkOS RBAC: permission slugs from the session JWT. */
  permissions: z.array(z.string()),
  /**
   * Clipbook staff, decided by the API (`isClipbookStaff`: the permission AND a session in
   * Clipbook's own organization). The SPA reads this rather than the permission, which cannot tell
   * a customer's admin from Clipbook's (CLI-905).
   */
  staff: z.boolean(),
})

/**
 * `user: null` is the signed-out answer, and it comes back with a 200. Being signed out is the
 * expected state of a visitor asking who they are, not an error.
 *
 * `noAccess: true` distinguishes the third state: a *valid* WorkOS session whose user holds no
 * organization membership. Access is invitation-only — membership is the grant — so these
 * visitors are authenticated but unadmitted, and the landing page tells them to ask for an
 * invitation rather than showing sign-in (which would loop them right back here).
 */
export const sessionResponseSchema = z.object({
  user: sessionUserSchema.nullable(),
  noAccess: z.boolean().optional(),
})

export type SessionUser = z.infer<typeof sessionUserSchema>
export type SessionResponse = z.infer<typeof sessionResponseSchema>
